import React, { useMemo } from 'react';
import { Card } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import type { OrderFunnel } from '../../types/dashboardFounder';
import { ChartSkeleton } from '../dashboard/ChartSkeleton';
import { DashboardEmptyState } from '../dashboard/DashboardEmptyState';

interface OrderFunnelChartProps {
  data: OrderFunnel;
  loading?: boolean;
}

const STAGES = [
  { key: 'created' as const, label: 'Created', color: '#94a3b8' },
  { key: 'confirmed' as const, label: 'Confirmed', color: '#3b82f6' },
  { key: 'delivered' as const, label: 'Delivered', color: '#22c55e' },
  { key: 'cancelled' as const, label: 'Cancelled', color: '#ef4444' },
];

export const OrderFunnelChart: React.FC<OrderFunnelChartProps> = ({
  data,
  loading = false,
}) => {
  const chartData = useMemo(() => {
    return STAGES.map(({ key, label }) => ({
      name: label,
      value: data[key] ?? 0,
      key,
    }));
  }, [data]);

  const total = chartData.reduce((s, d) => s + d.value, 0);
  const hasData = total > 0;

  if (loading) {
    return (
      <Card className="founder-chart-card" style={{ borderRadius: 12 }}>
        <h3 className="founder-chart-title">Order Funnel</h3>
        <ChartSkeleton height={240} />
      </Card>
    );
  }

  if (!hasData) {
    return (
      <Card className="founder-chart-card" style={{ borderRadius: 12 }}>
        <h3 className="founder-chart-title">Order Funnel</h3>
        <DashboardEmptyState
          message="Chưa có dữ liệu"
          suggestion="Chọn khoảng ngày khác."
        />
      </Card>
    );
  }

  return (
    <Card className="founder-chart-card" style={{ borderRadius: 12 }}>
      <h3 className="founder-chart-title">Order Funnel</h3>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 8, right: 24, left: 60, bottom: 8 }}
        >
          <XAxis type="number" tick={{ fontSize: 11 }} />
          <YAxis
            type="category"
            dataKey="name"
            width={55}
            tick={{ fontSize: 11 }}
          />
          <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={28}>
            <LabelList
              dataKey="value"
              position="right"
              formatter={(v: number) => v.toLocaleString()}
            />
            {chartData.map((entry, i) => (
              <Cell key={entry.key} fill={STAGES[i]?.color ?? '#94a3b8'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
};
